import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UNIT_NUMBER } from '@core/enum/dashboard';
import { ChartData, ChartOptions } from 'chart.js';
import { ChartModule } from 'primeng/chart';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TranslateModule } from '@ngx-translate/core';
@Component({
  selector: 'app-bar-line-chart',
  standalone: true,
  imports: [CommonModule, ChartModule, SelectButtonModule, FormsModule, SkeletonModule, TranslateModule],
  templateUrl: './bar-line-chart.component.html',
  styleUrl: './bar-line-chart.component.scss',
  providers: [DecimalPipe]
})
export class BarLineChartComponent implements OnInit, OnChanges {
  @Input() loading: boolean = false;
  @Input() data: ChartData;
  @Input() title: string = '';
  @Input() totalAmount: number = 0;
  @Input() dataType: UNIT_NUMBER = UNIT_NUMBER.NUMBER;
  @Input() datesSelected: Date[] = [];

  readonly UNIT_NUMBER = UNIT_NUMBER;
  tabOptions = [
    {
      title: 'Line',
      value: 'LINE'
    },
    {
      title: 'Bar',
      value: 'BAR'
    }
  ];

  chartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            let label = context.dataset.label || '';
            const value = this.decimalPipe.transform(context.parsed.y, '1.0-2') || '0';
            if (this.dataType === UNIT_NUMBER.CURRENCY) {
              return `${label}:$${value}`;
            } else if (this.dataType === UNIT_NUMBER.PERCENT) {
              return `${label}:${value}%`;
            } else {
              return `${label}:${value}`;
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 5,
          callback: value => value
        },
        grid: {
          drawTicks: false,
          lineWidth: 0.3
        },
        border: {
          display: false
        }
      }
    },
    datasets: {
      bar: {
        barPercentage: 0.5,
        categoryPercentage: 0.8,
        maxBarThickness: 20
      }
    }
  };

  tabActive: 'BAR' | 'LINE' | 'DOUGHNUT' = 'BAR';

  constructor(private decimalPipe: DecimalPipe) {}

  ngOnInit(): void {
    this.updateTabOptions();
    this.updateChartOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['datesSelected']?.currentValue) {
      this.updateTabOptions();
    }
  }

  updateTabOptions(): void {
    if (this.datesSelected?.[0]?.toDateString() === this.datesSelected?.[1]?.toDateString()) {
      this.tabOptions = [{ title: 'Doughnut', value: 'DOUGHNUT' }];
      this.tabActive = 'DOUGHNUT';
    } else {
      this.tabOptions = [
        { title: 'Line', value: 'LINE' },
        { title: 'Bar', value: 'BAR' }
      ];
      this.tabActive = 'BAR';
    }
  }

  updateChartOptions(): void {
    if (this.dataType === UNIT_NUMBER.PERCENT) {
      this.chartOptions = {
        ...this.chartOptions,
        scales: {
          ...this.chartOptions.scales,
          y: {
            ...this.chartOptions.scales?.['y'],
            ticks: {
              maxTicksLimit: 5,
              callback: value => value + '%'
            }
          }
        }
      };
    }
    if (this.dataType === UNIT_NUMBER.CURRENCY) {
      this.chartOptions = {
        ...this.chartOptions,
        scales: {
          ...this.chartOptions.scales,
          y: {
            ...this.chartOptions.scales?.['y'],
            ticks: {
              maxTicksLimit: 5,
              callback: value => '$' + value + '.00'
            }
          }
        }
      };
    }
  }
}
